import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { container } from '@infrastructure/di/container';
import { QUERY_KEYS } from '@shared/constants';
import type {
  CreateCollectionPayload,
  ExportFormat,
  ImportFormat,
  UpdateCollectionPayload,
  VariableInput,
} from '@core/repositories/CollectionRepository';

export const useCollections = (workspaceId: string | undefined) => {
  return useQuery({
    queryKey: QUERY_KEYS.collections(workspaceId ?? ''),
    queryFn: () => container.collections.list(workspaceId as string),
    enabled: Boolean(workspaceId),
  });
};

export const useCollection = (id: string | undefined) => {
  return useQuery({
    queryKey: QUERY_KEYS.collection(id ?? ''),
    queryFn: () => container.collections.get(id as string),
    enabled: Boolean(id),
  });
};

export const useCreateCollection = (workspaceId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateCollectionPayload) =>
      container.collections.create(workspaceId, payload),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.collections(workspaceId) }),
  });
};

export const useUpdateCollection = (id: string, workspaceId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateCollectionPayload) => container.collections.update(id, payload),
    onSuccess: (collection) => {
      queryClient.setQueryData(QUERY_KEYS.collection(id), collection);
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.collections(workspaceId) });
    },
  });
};

export const useDuplicateCollection = (workspaceId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => container.collections.duplicate(id),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.collections(workspaceId) }),
  });
};

export const useReorderCollections = (workspaceId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (orderedIds: string[]) => container.collections.reorder(workspaceId, orderedIds),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.collections(workspaceId) }),
  });
};

export const useImportCollection = (workspaceId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ format, payload }: { format: ImportFormat; payload: string }) =>
      container.collections.import(workspaceId, format, payload),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.collections(workspaceId) }),
  });
};

export const useExportCollection = () => {
  return useMutation({
    mutationFn: ({ id, format }: { id: string; format: ExportFormat }) =>
      container.collections.exportCollection(id, format),
  });
};

export const useDeleteCollection = (workspaceId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => container.collections.remove(id),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.collections(workspaceId) }),
  });
};

export const useToggleCollectionFavorite = (workspaceId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isFavorited }: { id: string; isFavorited: boolean }) =>
      isFavorited ? container.collections.unfavorite(id) : container.collections.favorite(id),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.collection(id) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.collections(workspaceId) });
    },
  });
};

export const useCollectionVariables = (id: string | undefined) => {
  return useQuery({
    queryKey: QUERY_KEYS.collectionVariables(id ?? ''),
    queryFn: () => container.collections.listVariables(id as string),
    enabled: Boolean(id),
  });
};

export const useSyncCollectionVariables = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: VariableInput[]) => container.collections.syncVariables(id, variables),
    onSuccess: (variables) => {
      queryClient.setQueryData(QUERY_KEYS.collectionVariables(id), variables);
    },
  });
};
